import { Subscriptions } from "@bezoned/shared/features/subscriptions/subscriptions.constants.js";
import {
  asStableCallback,
  useStableCallback,
} from "@bezoned/shared/hooks/use-stable-callback.js";
import { Body1, Spinner, Tooltip } from "@fluentui/react-components";
import { useMutation } from "@tanstack/react-query";
import { createRef, useCallback, useEffect, useMemo, useState } from "react";
import type { RefObject } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { CSSTransition, TransitionGroup } from "react-transition-group";

import { teamReorder } from "../../../services/api.service.js";
import { recordErrorInSentry } from "../../../utils/init-sentry.js";
import { STARTER_OFFICE_LIMIT_REACHED_MESSAGE } from "../../subscription/starter-plan-copy.js";
import { useSubscription } from "../../subscription/subscription.context.js";
import { TeamCreateButton } from "../team-create/team-create-button.js";
import { TeamCreateDialog } from "../team-create/team-create-dialog.js";
import { useCreateTeamDialog } from "../team-create/use-create-team-dialog.js";
import { TeamEmptyState } from "../team-list/team-empty-state.js";
import { TeamSyntheticListItem } from "../team-list/team-synthetic-list-item.js";
import { useTeamCreationLobby } from "../team-list/use-team-creation-lobby.js";
import { TeamEditDialog } from "./team-edit-dialog.js";
import { TeamEditListItem } from "./team-edit-list-item.js";

interface TeamEditListProps {
  dispatchFailureToast: (params: { title?: string; message: string }) => void;
}

type TransitionRef = RefObject<HTMLDivElement | null>;

export const TeamEditList = ({ dispatchFailureToast }: TeamEditListProps) => {
  const { planId } = useSubscription();
  const {
    jobsQuery,
    renderModel,
    teamsQuery,
    dismissFailedJob,
    dismissingJobId,
  } = useTeamCreationLobby(true, { includeRole: true });
  const teams = teamsQuery.data?.teams ?? [];
  const starterCreateDisabled = Boolean(
    planId === Subscriptions.StarterPlan &&
    teamsQuery.data &&
    teamsQuery.data.meta.totalOfficeCount +
      teamsQuery.data.meta.activeCreateOfficeJobCount >
      0
  );

  const [localTeams, setLocalTeams] = useState(teams);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isCreateTeamDialogOpen, setIsCreateTeamDialogOpen] = useState(false);
  const transitionKeySignature = [
    "create-button",
    ...renderModel.activeItems.map((item) => item.key),
    ...renderModel.failedItems.map((item) => item.key),
    ...localTeams.map((team) => team.id),
  ].join("|");
  const transitionRefs = useMemo(() => {
    const refs = new Map<string, TransitionRef>();
    for (const key of transitionKeySignature.split("|")) {
      refs.set(key, createRef<HTMLDivElement>());
    }
    return refs;
  }, [transitionKeySignature]);

  const getTransitionRef = (key: string): TransitionRef => {
    const transitionRef = transitionRefs.get(key);
    if (!transitionRef) {
      throw new Error(`Missing transition ref for key: ${key}`);
    }
    return transitionRef;
  };

  const { props: createTeamDialogProps, setOpen: setCreateTeamDialogOpen } =
    useCreateTeamDialog({
      onCreateTeamError: (error) => {
        if (error.code === "starter-office-limit-reached") {
          dispatchFailureToast({ message: error.message });
        }
      },
      open: isCreateTeamDialogOpen,
      setOpen: setIsCreateTeamDialogOpen,
    });

  useEffect(() => {
    setLocalTeams(teams);
  }, [teams]);

  const moveTeam = useCallback((fromIndex: number, toIndex: number) => {
    setLocalTeams((prev) => {
      const updated = [...prev];
      const [removed] = updated.splice(fromIndex, 1);
      if (removed) {
        updated.splice(toIndex, 0, removed);
      }
      return updated;
    });
  }, []);

  const reorderMutation = useMutation({
    mutationFn: (params: { msTeamId: string; newIndex: number }) =>
      teamReorder(params),
    onError: (error) => {
      recordErrorInSentry(
        error instanceof Error ? error : new Error(String(error))
      );
      setLocalTeams(teams);
      dispatchFailureToast({
        message: "Failed to update the order. Please try again.",
      });
    },
  });

  const selectedTeam = selectedTeamId
    ? (localTeams.find((team) => team.id === selectedTeamId) ?? null)
    : null;
  const handleClose153 = useStableCallback(() => setIsEditDialogOpen(false));

  const handleClick89 = useStableCallback(() => {
    if (!starterCreateDisabled) {
      setCreateTeamDialogOpen(true);
    }
  });

  if (teamsQuery.isLoading || jobsQuery.isLoading) {
    return (
      <div className="flex flex-col items-center gap-4 py-12">
        <Spinner size="large" />
        <Body1>Checking permissions...</Body1>
      </div>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <TransitionGroup className="flex flex-col">
        <CSSTransition
          classNames="fade"
          key="create-button"
          nodeRef={getTransitionRef("create-button")}
          timeout={200}
        >
          <div ref={getTransitionRef("create-button")}>
            {starterCreateDisabled ? (
              <Tooltip
                content={STARTER_OFFICE_LIMIT_REACHED_MESSAGE}
                relationship="label"
              >
                <span className="block">
                  <TeamCreateButton disabled onClick={handleClick89} />
                </span>
              </Tooltip>
            ) : (
              <TeamCreateButton onClick={handleClick89} />
            )}
          </div>
        </CSSTransition>

        {renderModel.activeItems.map((item) => (
          <CSSTransition
            classNames="fade"
            key={item.key}
            nodeRef={getTransitionRef(item.key)}
            timeout={200}
          >
            <div ref={getTransitionRef(item.key)}>
              <TeamSyntheticListItem
                kind="active"
                requestedName={item.job.requestedName}
                visualStatus={item.visualStatus}
              />
            </div>
          </CSSTransition>
        ))}

        {renderModel.failedItems.map((item) => (
          <CSSTransition
            classNames="fade"
            key={item.key}
            nodeRef={getTransitionRef(item.key)}
            timeout={200}
          >
            <div ref={getTransitionRef(item.key)}>
              <TeamSyntheticListItem
                dismissDisabled={dismissingJobId === item.job.jobId}
                kind="failed"
                onDismiss={asStableCallback(() =>
                  dismissFailedJob(item.job.jobId)
                )}
                requestedName={item.job.requestedName}
              />
            </div>
          </CSSTransition>
        ))}

        {localTeams.map((team, index) => (
          <CSSTransition
            classNames="fade"
            key={team.id}
            nodeRef={getTransitionRef(team.id)}
            timeout={200}
          >
            <div ref={getTransitionRef(team.id)}>
              <TeamEditListItem
                highlighted={renderModel.highlightedTeamIds.has(team.id)}
                index={index}
                moveTeam={moveTeam}
                onDrop={asStableCallback((msTeamId, newIndex) =>
                  reorderMutation.mutate({ msTeamId, newIndex })
                )}
                onEdit={asStableCallback(() => {
                  if (team.accessRestrictedReason) {
                    return;
                  }

                  setSelectedTeamId(team.id);
                  setIsEditDialogOpen(true);
                })}
                team={team}
              />
            </div>
          </CSSTransition>
        ))}

        {localTeams.length === 0 &&
          renderModel.activeItems.length === 0 &&
          renderModel.failedItems.length === 0 &&
          jobsQuery.isError && (
            <TeamEmptyState message="No BeZoned teams found. Please refresh and try again." />
          )}
      </TransitionGroup>

      <TeamCreateDialog {...createTeamDialogProps} />

      <TeamEditDialog
        onClose={handleClose153}
        open={isEditDialogOpen}
        team={selectedTeam}
      />
    </DndProvider>
  );
};
